/**
 * Effect-only hook that drives the coverage screen's data lifecycle: primes
 * the shared single-order accumulator for the route's order, then — once the
 * general order, auth-on-file, and coverage domains have all settled —
 * persists J-codes from the auth-on-file, publishes the primary-coverage
 * payer name to the single-order layout, and fires the one-time
 * EV_SCREEN_VIEWED analytics event.
 *
 * The prime/abort work stays in its own `[orderId, orgId]` effect. `abortAll`
 * must run ONLY on unmount or order change: merging it into the data-settle
 * effect makes the cleanup run on every data arrival, which aborts the
 * page's own in-flight domain requests and restarts them in a loop (loader
 * sticks and flickers). The settle effect is cleanup-free and its work is
 * idempotent (one-time event ref, no-op store write, string state), so it is
 * safe to re-run whenever the derived `dataReady` flag or the primary payer
 * name flips — and it must not re-prime on data arrival.
 *
 * Extracted from {@link CoverageInfo} so the page stays declarative and the
 * effects are unit-testable in isolation.
 *
 * @module coverageInfo/hooks/useCoverageScreenInit
 */
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import type {
  AuthOnFileDetailsResponse,
  CoverageDetailsResponse,
  GeneralOrderDetailsResponse,
} from "../../../context/dataAccumulator/types";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import { setJCodesFromAuthOnFile } from "../../../utils/setJCodesInLocalStorage";
import { apiWorkerManager } from "../../../workerManagers/workerManager";

/** Arguments for {@link useCoverageScreenInit}. */
interface UseCoverageScreenInitArgs {
  /** Route param of the PA order; the prime fetch stays inert when undefined. */
  orderId: string | undefined;
  /** Org id scoping the accumulator fetch and the screen-viewed event. */
  orgId: string;
  /** General order details response from the shared accumulator. */
  generalOrderDetails: GeneralOrderDetailsResponse | null;
  /** Auth-on-file response from the shared accumulator. */
  authOnFileDetails: AuthOnFileDetailsResponse | null;
  /** Coverage response from the shared accumulator. */
  coverageDetails: CoverageDetailsResponse | null;
  /** Whether any domain the coverage screen renders is still loading. */
  loading: boolean;
  /** Accumulator fetch trigger; `{ force: false }` reuses the per-order cache. */
  fetchAll: (
    orderId: string,
    orgId: string,
    options?: { force?: boolean },
  ) => Promise<void>;
  /** Aborts every in-flight accumulator domain request. */
  abortAll: () => void;
}

/**
 * Primes the shared accumulator for `orderId`, and once the order,
 * auth-on-file, and coverage data have all settled, persists J-codes from the
 * auth-on-file, sets the primary payer name on the single-order layout, and
 * fires the one-time EV_SCREEN_VIEWED analytics event.
 *
 * @param args - Route/accumulator inputs plus the accumulator's fetch
 *   orchestration helpers
 */
export const useCoverageScreenInit = ({
  orderId,
  orgId,
  generalOrderDetails,
  authOnFileDetails,
  coverageDetails,
  loading,
  fetchAll,
  abortAll,
}: UseCoverageScreenInitArgs): void => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { setPayerName } = usePayerNameContextFromSingleOrderLayout();
  const isEventTriggerRef = useRef(false);

  // The settle effect's dep array can't carry the data objects themselves —
  // refetches swap object identities without the settle condition changing.
  // These derived values flip only when the settle condition or the primary
  // payer name changes, which is exactly when the side effects must run.
  const dataReady =
    Boolean(generalOrderDetails) &&
    Boolean(authOnFileDetails) &&
    Boolean(coverageDetails) &&
    !loading;
  const primaryPayerName =
    coverageDetails?.coverages.find(
      (coverage) => coverage.coverage_type === "primary",
    )?.payer_name ?? "";

  // Prime the accumulator when the order is known; abort in-flight requests
  // on unmount or order change. This must stay its own effect — the cleanup
  // below must NOT run on data arrival (see module docs).
  useEffect(() => {
    if (orderId && orgId) {
      fetchAll(orderId, orgId, { force: false });
    }
    return () => abortAll();
    // fetchAll / abortAll are referentially stable in the accumulator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orgId]);

  useEffect(() => {
    if (
      dataReady &&
      generalOrderDetails &&
      authOnFileDetails &&
      coverageDetails
    ) {
      setJCodesFromAuthOnFile(authOnFileDetails.auth_on_file, orderId ?? "");

      const demographics = generalOrderDetails.order.demographics;

      setPayerName(primaryPayerName);

      !isEventTriggerRef.current &&
        apiWorkerManager.enqueueAndProcess({
          patient_id:
            demographics?.patient_id ?? demographics?.patient_mrn ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name: EventsName.EV_SCREEN_VIEWED,
          org_id: orgId,
          order_id: orderId ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            patient_name:
              demographics?.first_name + " " + demographics?.last_name,
            patient_mrn:
              demographics?.patient_id ?? demographics?.patient_mrn ?? "",
            payer_name: primaryPayerName,
          },
        });
      isEventTriggerRef.current = true;
    }
    // The data objects are consumed at latest value and gated by dataReady,
    // which flips whenever they settle or refresh; setPayerName is a useState
    // setter, so the deliberate dep array is exhaustive in effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, primaryPayerName, user]);
};
